import React, { useCallback, useEffect, useState } from "react";
import {
  fetchParticipantImpDispenses,
  fetchParticipantImpSummary,
} from "../epsf/epsfService";
import { displayOrDash, pickField } from "./clinicalHistoryUtils";

function formatWhen(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function strengthLabel(row) {
  const strength = pickField(row, "Strength");
  if (strength) return String(strength);
  const value = pickField(row, "StrengthValue");
  const unit = pickField(row, "StrengthUnit");
  if (value || unit) return [value, unit].filter(Boolean).join(" ");
  return "";
}

/**
 * Read-only Study IMP exposure from ePSF dispenses.
 * Does not write Medication History.
 */
export default function StudyImpClinicalSection({ volunteerDemRecordId }) {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyKey, setHistoryKey] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);

  const load = useCallback(async () => {
    if (!volunteerDemRecordId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await fetchParticipantImpSummary(volunteerDemRecordId);
      if (!result.success) {
        setError(result.message || "Could not load Study IMP history.");
        setRows([]);
        return;
      }
      setRows(result.rows || []);
    } catch (err) {
      setError(err?.message || "Could not load Study IMP history.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [volunteerDemRecordId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleHistory = async (row) => {
    const studyId = String(pickField(row, "StudyId") || "");
    const siteId = String(pickField(row, "SiteId") || "");
    const impProductId = String(pickField(row, "ImpProductId") || "");
    const key = `${studyId}|${siteId}|${impProductId}`;
    if (historyKey === key) {
      setHistoryKey("");
      setHistoryRows([]);
      return;
    }
    setHistoryBusy(true);
    setHistoryKey(key);
    try {
      const result = await fetchParticipantImpDispenses({
        participantId: volunteerDemRecordId,
        studyId,
        siteId,
        impProductId,
      });
      if (!result.success) {
        setError(result.message || "Could not load dispensing history.");
        setHistoryRows([]);
        return;
      }
      setHistoryRows(result.rows || []);
    } catch (err) {
      setError(err?.message || "Could not load dispensing history.");
      setHistoryRows([]);
    } finally {
      setHistoryBusy(false);
    }
  };

  if (!volunteerDemRecordId) return null;

  return (
    <div className="pm-clinical-study-imp mt-3">
      <div className="pm-clinical-panel__head">
        <h3 className="pm-clinical-panel__title">
          <i className="fas fa-capsules me-2" aria-hidden="true" />
          Study IMP
        </h3>
        <p className="pm-clinical-panel__hint mb-0">
          Pharmacy accountability from ePSF. Not written to Medication History.
        </p>
      </div>

      {error ? <p className="epsf-list-page__error">{error}</p> : null}
      {loading ? <p className="text-muted small">Loading Study IMP…</p> : null}

      {!loading && !rows.length ? (
        <p className="text-muted small mb-0">
          No Study IMP dispenses recorded for this participant.
        </p>
      ) : null}

      {rows.length ? (
        <div className="pm-clinical-cards pm-thin-scroll">
          {rows.map((row, index) => {
            const studyId = String(pickField(row, "StudyId") || "");
            const siteId = String(pickField(row, "SiteId") || "");
            const impProductId = String(pickField(row, "ImpProductId") || "");
            const key = `${studyId}|${siteId}|${impProductId}`;
            const open = historyKey === key;
            const strength = strengthLabel(row);
            const dispenseCount = Number(
              pickField(row, "DispenseCount") || 0,
            );
            const returned = Number(
              pickField(row, "TotalReturnedQuantity") || 0,
            );
            return (
              <article key={key || index} className="pm-clinical-card">
                <div className="pm-clinical-card__icon" aria-hidden="true">
                  <i className="fas fa-capsules" />
                </div>
                <div className="pm-clinical-card__body">
                  <h4 className="pm-clinical-card__title">
                    {displayOrDash(
                      pickField(row, "ProductName") ||
                        pickField(row, "ProductCode"),
                    )}
                  </h4>
                  <div className="pm-clinical-card__meta">
                    {strength ? <span>{strength}</span> : null}
                    {pickField(row, "Route") ? (
                      <span>{pickField(row, "Route")}</span>
                    ) : null}
                    <span>
                      Study:{" "}
                      {displayOrDash(
                        pickField(row, "StudyName") ||
                          pickField(row, "StudyNumber"),
                      )}
                    </span>
                    <span>
                      Site:{" "}
                      {displayOrDash(
                        pickField(row, "SiteName") ||
                          pickField(row, "SiteNumber"),
                      )}
                    </span>
                    <span>
                      Dispensed {dispenseCount} time
                      {dispenseCount === 1 ? "" : "s"}
                    </span>
                    <span>
                      Last dispensed: {formatWhen(pickField(row, "LastDispensedAt"))}
                    </span>
                    {returned > 0 ? (
                      <span>Participant returned: {returned}</span>
                    ) : null}
                    <span>Source: ePSF</span>
                  </div>
                  {open ? (
                    <div className="mt-2">
                      {historyBusy ? (
                        <p className="small text-muted mb-0">Loading history…</p>
                      ) : (
                        <ul className="small mb-0 ps-3">
                          {(historyRows || []).map((h, hi) => (
                            <li key={String(pickField(h, "DispenseTxnId") || hi)}>
                              {formatWhen(pickField(h, "TxnAt"))}
                              {" · "}
                              {pickField(h, "Quantity") ?? "—"}
                              {pickField(h, "BatchNumber")
                                ? ` · batch ${pickField(h, "BatchNumber")}`
                                : ""}
                              {Number(pickField(h, "ReturnedQuantity") || 0) > 0
                                ? ` · returned ${pickField(h, "ReturnedQuantity")}`
                                : ""}
                              {pickField(h, "DispensedByName")
                                ? ` · ${pickField(h, "DispensedByName")}`
                                : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="pm-clinical-card__actions">
                  <button
                    type="button"
                    className="pm-clinical-card__btn"
                    title={open ? "Hide history" : "View dispensing history"}
                    aria-label={open ? "Hide history" : "View dispensing history"}
                    onClick={() => toggleHistory(row)}
                  >
                    <i className={`fas ${open ? "fa-chevron-up" : "fa-history"}`} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
